import React, { useCallback } from 'react';
import { adminAPI } from '../../services/api';
import AsyncTypeahead, { AsyncTypeaheadOption } from '../ui/AsyncTypeahead';

export type AccountTypeaheadOption = AsyncTypeaheadOption & {
  customerCount?: number;
};

type AccountTypeaheadProps = {
  value: AccountTypeaheadOption | null;
  onChange: (value: AccountTypeaheadOption | null) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  allowCreate?: boolean;
};

export default function AccountTypeahead({
  value,
  onChange,
  placeholder = 'Search account by name...',
  className,
  disabled,
  allowCreate = false,
}: AccountTypeaheadProps) {
  const loadAccounts = useCallback(async (query: string): Promise<AccountTypeaheadOption[]> => {
    const response = await adminAPI.getAccounts({
      search: query || undefined,
      limit: 10,
      page: 1,
    });

    const payload = response?.data;
    const accounts = Array.isArray(payload?.accounts) ? payload.accounts : [];

    return accounts
      .filter((account: any) => account?.id)
      .map((account: any) => ({
        id: String(account.id),
        label: account.name || `Account ${account.id}`,
        description: `${Number(account.customer_count || 0)} customer${Number(account.customer_count || 0) === 1 ? '' : 's'} attached`,
        customerCount: Number(account.customer_count || 0),
      } satisfies AccountTypeaheadOption));
  }, []);

  const createAccount = useCallback(async (query: string): Promise<AccountTypeaheadOption | null> => {
    const name = query.trim();
    if (!name) {
      return null;
    }

    const response = await adminAPI.createAccount({ name });
    const account = response?.data;
    if (!account?.id) {
      return null;
    }

    const customerCount = Number(account.customer_count || 0);
    return {
      id: String(account.id),
      label: account.name || name,
      description: `${customerCount} customer${customerCount === 1 ? '' : 's'} attached`,
      customerCount,
    };
  }, []);

  return (
    <AsyncTypeahead
      value={value}
      onChange={(option) => onChange(option as AccountTypeaheadOption | null)}
      loadOptions={loadAccounts}
      onCreateOption={allowCreate ? createAccount : undefined}
      createOptionLabel={(query) => `Create account "${query}"`}
      placeholder={placeholder}
      emptyMessage={allowCreate ? 'No accounts matched this name.' : 'No accounts matched your search.'}
      className={className}
      disabled={disabled}
    />
  );
}
